import React from 'react';
import Icon from '../../../components/AppIcon';
import { Checkbox } from '../../../components/ui/Checkbox';

const ConsentPrivacyStep = ({ formData, onChange, errors }) => {
  const dataUsageItems = [
    {
      id: 'matching',
      label: 'Use my information for helper matching',
      description: 'Required to connect you with appropriate helpers',
      required: true
    },
    {
      id: 'analytics',
      label: 'Include my data in anonymized platform analytics',
      description: 'Helps improve the platform for all users',
      required: false
    },
    {
      id: 'research',
      label: 'Allow my anonymized data for mental health research',
      description: 'Contributes to broader mental health understanding',
      required: false
    },
    {
      id: 'communications',
      label: 'Send me helpful resources and platform updates',
      description: 'Occasional emails about mental health resources',
      required: false
    }
  ];

  const privacyControls = [
    {
      id: 'anonymousDisplay',
      label: 'Display my profile anonymously to helpers',
      description: 'Helpers will see limited information until you connect'
    },
    {
      id: 'hideFromDirectory',
      label: 'Hide my profile from public helper directory',
      description: 'Only matched helpers can see your information'
    },
    {
      id: 'requireApproval',
      label: 'Require my approval before sharing session notes',
      description: 'You control what information is shared'
    }
  ];

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-foreground mb-2">
          Consent & Privacy Controls
        </h3>
        <p className="text-sm text-muted-foreground">
          You control how your information is used and shared
        </p>
      </div>
      <div className="p-4 bg-primary/5 rounded-lg border border-primary/20">
        <div className="flex items-start gap-3">
          <Icon name="Shield" size={20} className="text-primary mt-0.5" />
          <div className="text-sm">
            <p className="text-foreground font-medium mb-1">Your Data, Your Control</p>
            <p className="text-muted-foreground">
              All information is encrypted end-to-end and stored securely. You can export, modify, or delete your data at any time. We never share identifiable information without explicit consent.
            </p>
          </div>
        </div>
      </div>
      <div className="space-y-4">
        <label className="text-sm font-medium text-foreground">
          Data Usage Permissions
        </label>
        {dataUsageItems?.map((item) => (
          <div key={item?.id} className="space-y-2">
            <Checkbox
              label={
                <span>
                  {item?.label}
                  {item?.required && <span className="text-error ml-1">*</span>}
                </span>
              }
              description={item?.description}
              checked={formData?.dataConsent?.[item?.id] || false}
              onChange={(e) => {
                const newConsent = { ...formData?.dataConsent, [item?.id]: e?.target?.checked };
                onChange('dataConsent', newConsent);
              }}
              required={item?.required}
            />
          </div>
        ))}
        {errors?.dataConsent && (
          <p className="text-sm text-error">{errors?.dataConsent}</p>
        )}
      </div>
      <div className="space-y-4">
        <label className="text-sm font-medium text-foreground">
          Privacy Settings
        </label>
        {privacyControls?.map((control) => (
          <Checkbox
            key={control?.id}
            label={control?.label}
            description={control?.description}
            checked={formData?.privacySettings?.[control?.id] || false}
            onChange={(e) => {
              const newSettings = { ...formData?.privacySettings, [control?.id]: e?.target?.checked };
              onChange('privacySettings', newSettings);
            }}
          />
        ))}
      </div>
      <div className="space-y-4">
        <label className="text-sm font-medium text-foreground">
          Required Agreements
        </label>
        <Checkbox
          label={
            <span>
              I agree to the{' '}
              <a href="#" className="text-primary hover:underline">
                Terms of Service
              </a>{' '}
              and{' '}
              <a href="#" className="text-primary hover:underline">
                Privacy Policy
              </a>
              <span className="text-error ml-1">*</span>
            </span>
          }
          checked={formData?.agreeToTerms || false}
          onChange={(e) => onChange('agreeToTerms', e?.target?.checked)}
          required
          error={errors?.agreeToTerms}
        />
        <Checkbox
          label={
            <span>
              I understand this platform provides support but is not a substitute for emergency services
              <span className="text-error ml-1">*</span>
            </span>
          }
          checked={formData?.understandLimitations || false}
          onChange={(e) => onChange('understandLimitations', e?.target?.checked)}
          required
          error={errors?.understandLimitations}
        />
      </div>
      <div className="p-4 bg-muted/50 rounded-lg border border-border">
        <div className="flex items-start gap-3">
          <Icon name="Lock" size={20} className="text-foreground mt-0.5" />
          <div className="text-sm">
            <p className="text-foreground font-medium mb-1">Security & Compliance</p>
            <div className="space-y-1 text-muted-foreground">
              <p>✓ ISO 27001 Certified Security</p>
              <p>✓ HIPAA Compliant Data Handling</p>
              <p>✓ End-to-End Encryption</p>
              <p>✓ Regular Security Audits</p>
              <p>✓ Data Residency Compliance</p>
            </div>
          </div>
        </div>
      </div>
      <div className="p-4 bg-warning/10 rounded-lg border border-warning/20">
        <div className="flex items-start gap-3">
          <Icon name="AlertCircle" size={20} className="text-warning mt-0.5" />
          <div className="text-sm">
            <p className="text-foreground font-medium mb-1">Duty of Care Notice</p>
            <p className="text-muted-foreground">
              If helpers identify immediate safety concerns during sessions, they are required to follow duty of care protocols which may include contacting emergency services or designated contacts. This is for your safety and wellbeing.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConsentPrivacyStep;